<script>
	export default {
		props: {
			labelId: {
				type: String
			},
			showArrows: {
				type: Boolean
			},
			showPagination: {
				type: Boolean
			},
			totalSlides: {
				type: Number,
				default: 1
			},
			autoplay: {
				type: Boolean,
				default: false
			},
			autoplayInterval: {
				type: Number,
				default: 5000
			}
		},
		data() {
			return {
				currentSlide: 0,
				autoplayTimer: null,
				isAutoplaying: true,
				isExplicitlyPaused: false,
				intersectionObserver: null
			};
		},
		methods: {
			goToSlide(slide, setSlideFocus = false) {
				this.currentSlide = slide;

				this.$emit('slide-change', slide, setSlideFocus);
			},
			nextSlide(setSlideFocus = false) {
				let targetSlide = this.currentSlide + 1;

				if (targetSlide > this.totalSlides - 1) {
					targetSlide = 0;
				}
				this.goToSlide(targetSlide, setSlideFocus);
			},
			previousSlide(setSlideFocus = false) {
				let targetSlide = this.currentSlide - 1;

				if (targetSlide < 0) {
					targetSlide = this.totalSlides - 1;
				}

				this.goToSlide(targetSlide, setSlideFocus);
			},
			onPaginationKeydown(event) {
				const key = event.key;

				if (key === 'ArrowRight') {
					this.nextSlide(true);
				} else if (key === 'ArrowLeft') {
					this.previousSlide(true);
				}
			},
			setAutoplayInterval() {
				if (this.autoplay && !this.autoplayTimer && !this.isExplicitlyPaused) {
					this.isAutoplaying = true;
					const interval = setInterval(() => {
						this.nextSlide();
					}, this.autoplayInterval);

					this.autoplayTimer = interval;
				}
			},
			clearAutoplayInterval() {
				if (this.autoplay && this.autoplayTimer) {
					this.isAutoplaying = false;
					clearInterval(this.autoplayTimer);
					this.autoplayTimer = null;
				}
			},
			toggleAutoplay() {
				if (this.isAutoplaying) {
					this.isExplicitlyPaused = true;
					this.clearAutoplayInterval();
				} else {
					this.isExplicitlyPaused = false;
					this.setAutoplayInterval();
				}
			},
			onIntersection(entries) {
				const intersecting = entries[0].isIntersecting;

				if (intersecting) {
					this.setAutoplayInterval();
				} else {
					this.clearAutoplayInterval();
				}
			}
		},
		mounted() {
			if (this.autoplay && this.totalSlides > 1) {
				this.setAutoplayInterval();

				this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
					threshold: 0.1
				});

				this.intersectionObserver.observe(this.$el);
			}
		},
		beforeDestroy() {
			this.clearAutoplayInterval();

			if (this.intersectionObserver) {
				this.intersectionObserver.disconnect();
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';

	.carousel {
		.carousel-body {
			display: flex;
			align-items: stretch;
		}

		.slide-container {
			position: relative;
			display: flex;
			flex-grow: 1;
			overflow: hidden;
			min-height: 20rem;
		}

		.arrow {
			font-size: 2rem;
			display: flex;
			align-items: center;
			border: none;
			background: none;
			color: $c-slate-dark;

			&:hover {
				color: $c-blue-dark;
			}

			&.left-arrow {
				padding-right: 1rem;
			}

			&.right-arrow {
				padding-left: 1rem;
			}
		}

		.carousel-controls {
			display: flex;
			justify-content: center;
			margin-top: 1rem;
		}

		.carousel-control {
			margin: 0 0.25rem;
			display: inline-flex;
			padding: 0.5rem;
			border-radius: 50%;
			color: $c-slate-dark;

			&:hover {
				background-color: $c-babyblue;
			}
		}

		.autoplay-control {
			line-height: 1;
			padding: 0.5rem 0.75rem;
		}

		.carousel-pagination-container {
			display: flex;
			justify-content: center;
			margin-top: 1rem;
		}

		.carousel-pagination-control {
			line-height: 0.5rem;
		}

		.pagination-dot {
			display: inline-block;
			height: 0.5rem;
			width: 0.5rem;
			border-radius: 50%;
			border: 1px solid currentColor;
			background-color: currentColor;
			transition: background-color 0.4s ease-in-out;
		}
	}
</style>
<template>
	<div
		class="carousel"
		aria-atomic="false"
		:aria-labelledby="labelId"
		aria-live="polite"
		aria-roledescription="carousel">
		<div class="carousel-body">
			<button
				v-if="showArrows && totalSlides > 1"
				type="button"
				@click="previousSlide()"
				class="arrow left-arrow"
				aria-label="Previous slide">
				<i class="fa-solid fa-chevron-left"></i>
			</button>
			<div
				class="slide-container"
				@mouseenter="clearAutoplayInterval"
				@mouseleave="setAutoplayInterval">
				<slot></slot>
			</div>
			<button
				v-if="showArrows && totalSlides > 1"
				type="button"
				@click="nextSlide()"
				class="arrow right-arrow"
				aria-label="Next Slide">
				<i class="fa-solid fa-chevron-right"></i>
			</button>
		</div>
		<div class="carousel-controls">
			<button
				v-if="autoplay"
				class="button -link carousel-control autoplay-control"
				:aria-label="isAutoplaying ? 'Pause carousel' : 'Play carousel'"
				@click="toggleAutoplay">
				<i
					class="fa-solid"
					:class="isAutoplaying ? 'fa-pause' : 'fa-play'"></i>
			</button>
			<div
				class="carousel-pagination-container"
				v-if="showPagination && totalSlides > 1 && totalSlides <= 6">
				<button
					v-for="slide in totalSlides"
					:key="slide"
					class="button -link carousel-control carousel-pagination-control"
					:class="{
						active: slide - 1 === currentSlide
					}"
					type="button"
					:aria-label="'Slide ' + slide + ' of ' + totalSlides"
					@click="goToSlide(slide - 1, true)"
					@keydown="onPaginationKeydown">
					<span class="pagination-dot"></span>
				</button>
			</div>
		</div>
	</div>
</template>
